import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Mail, Phone, Globe, User, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client.js";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Drawer } from "../../components/ui/Drawer.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { cn } from "../../lib/cn.js";

const FORM_TYPES = [
  { value: "", label: "All form types" },
  { value: "appointment", label: "Appointment Request" },
  { value: "contact_general", label: "General Contact" },
  { value: "new_build", label: "New Build Enquiry" },
  { value: "preconstruction", label: "Pre-Construction" },
  { value: "maintenance", label: "Maintenance" },
  { value: "career", label: "Career Application" },
  { value: "design_inquiry", label: "Design Inquiry" },
];

const STATUSES = ["new", "read", "responded", "closed"];
const STATUS_TONE = {
  new: "accent",
  read: "info",
  responded: "success",
  closed: "default",
};

export default function FormSubmissionsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [formType, setFormType] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    document.title = "Form Submissions · Admin";
  }, []);

  const params = {
    page,
    limit: 25,
    ...(search && { search }),
    ...(formType && { formType }),
    ...(status && { status }),
  };

  const { list, remove } = useResource("forms", { toastLabel: "submission" });
  const { data, isLoading } = list(params);

  useEffect(() => {
    setPage(1);
  }, [search, formType, status]);

  // CSV download — Bearer-protected endpoint, so we fetch as a blob and
  // trigger the download programmatically. A regular <a download> can't
  // attach the Authorization header.
  const downloadCsv = async () => {
    setExporting(true);
    try {
      const response = await api.get("/admin/forms/export", {
        params: { ...(formType && { formType }), ...(status && { status }) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `form-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Inbox</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Form Submissions</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} submission{data?.meta?.total === 1 ? "" : "s"}
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<Download className="h-4 w-4" />}
          onClick={downloadCsv}
          loading={exporting}
        >
          Export CSV
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border-subtle">
        <StatusTab label="All" value="" current={status} onClick={setStatus} />
        {STATUSES.map((s) => (
          <StatusTab key={s} label={s} value={s} current={status} onClick={setStatus} />
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, email, phone…"
          filters={
            <Select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              className="min-w-[200px]"
            >
              {FORM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          }
        />
        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => setOpenId(row._id)}
          columns={[
            {
              key: "name",
              label: "Submitted by",
              render: (r) => (
                <div className="min-w-0">
                  <p className="truncate font-medium text-text-primary">
                    {r.data?.name ?? r.data?.email ?? "(no name)"}
                  </p>
                  <p className="truncate text-xs text-text-muted">
                    {r.data?.email ?? r.data?.phone ?? r.sourcePage ?? "—"}
                  </p>
                </div>
              ),
            },
            {
              key: "formType",
              label: "Type",
              render: (r) => <Badge tone="info">{r.formType.replace(/_/g, " ")}</Badge>,
            },
            {
              key: "status",
              label: "Status",
              render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>,
            },
            {
              key: "createdAt",
              label: "Received",
              render: (r) => (
                <div className="flex flex-col">
                  <span className="text-text-secondary">{formatRelativeTime(r.createdAt)}</span>
                  <span className="text-xs text-text-muted">{formatDateTime(r.createdAt)}</span>
                </div>
              ),
            },
          ]}
          actions={(row) => (
            <RowActions onEdit={() => setOpenId(row._id)} onDelete={() => setConfirmDelete(row)} />
          )}
          empty={{
            icon: <Inbox className="h-5 w-5" />,
            title: search || formType || status ? "No matches" : "No submissions yet",
            description:
              search || formType || status
                ? "Try clearing the filters above."
                : "Forms submitted on the public site will land here.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      {openId && <SubmissionDrawer id={openId} onClose={() => setOpenId(null)} />}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete this submission?"
        description="The original notification email is unaffected — only the database record is removed."
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(confirmDelete._id, { onSuccess: () => setConfirmDelete(null) })
        }
      />
    </div>
  );
}

function StatusTab({ label, value, current, onClick }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "border-accent text-accent"
          : "border-transparent text-text-secondary hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

/* ============================================================================
   Detail drawer with status update + notes editor
   ============================================================================ */

function SubmissionDrawer({ id, onClose }) {
  const queryClient = useQueryClient();
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);

  // Fetch the full submission on open. We could colocate this in the parent,
  // but doing it here keeps the drawer self-contained.
  useEffect(() => {
    let cancelled = false;
    adminApi.get("forms", id).then((d) => {
      if (cancelled) return;
      setDoc(d);
      setNotes(d.notes ?? "");
      setPendingStatus(d.status);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const update = useMutation({
    mutationFn: (payload) => adminApi.update("forms", id, payload),
    onSuccess: (saved) => {
      setDoc(saved);
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      toast.success("Submission updated");
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? "Update failed"),
  });

  const save = () => {
    update.mutate({
      ...(pendingStatus !== doc?.status && { status: pendingStatus }),
      ...(notes !== (doc?.notes ?? "") && { notes }),
    });
  };

  if (!doc) {
    return (
      <Drawer open onClose={onClose} title="Loading…" width="md">
        <div className="py-12 text-center text-sm text-text-muted">Loading submission…</div>
      </Drawer>
    );
  }

  const dirty = pendingStatus !== doc.status || notes !== (doc.notes ?? "");

  return (
    <Drawer
      open
      onClose={onClose}
      title={doc.formType.replace(/_/g, " ")}
      description={`Received ${formatDateTime(doc.createdAt)} · ${formatRelativeTime(doc.createdAt)}`}
      width="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={save} loading={update.isPending} disabled={!dirty}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Status + responded-by */}
        <div className="flex items-center gap-3">
          <Select
            label="Status"
            value={pendingStatus}
            onChange={(e) => setPendingStatus(e.target.value)}
            className="flex-1"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          {doc.respondedBy && (
            <div className="flex-1 self-end">
              <p className="text-xs text-text-muted">Responded by</p>
              <p className="text-sm text-text-primary">{doc.respondedBy.email}</p>
              <p className="text-xs text-text-muted">{formatDateTime(doc.respondedAt)}</p>
            </div>
          )}
        </div>

        {/* Quick contact info */}
        <ContactStrip data={doc.data} sourcePage={doc.sourcePage} />

        {/* Full payload */}
        <div className="rounded-lg border border-border-subtle bg-bg-elevated-2">
          <div className="border-b border-border-subtle px-4 py-2 text-xs uppercase tracking-wider text-text-muted">
            Submission data
          </div>
          <dl className="divide-y divide-border-subtle">
            {Object.entries(doc.data ?? {}).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[140px_1fr] gap-3 px-4 py-2 text-sm">
                <dt className="truncate text-xs uppercase tracking-wider text-text-muted">{k}</dt>
                <dd className="break-words text-text-primary">
                  {typeof v === "object" ? (
                    <pre className="font-mono text-xs">{JSON.stringify(v, null, 2)}</pre>
                  ) : (
                    String(v)
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Notes */}
        <Textarea
          label="Internal notes"
          rows={5}
          hint="Visible to admins only. Use this to track follow-up actions."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {/* Audit metadata */}
        <div className="rounded-md bg-bg-elevated-2 p-3 text-xs text-text-muted">
          <p>IP: {doc.ipAddress || "—"}</p>
          <p className="mt-1 truncate">User agent: {doc.userAgent || "—"}</p>
        </div>
      </div>
    </Drawer>
  );
}

function ContactStrip({ data, sourcePage }) {
  if (!data) return null;
  const fields = [
    { icon: User, value: data.name },
    { icon: Mail, value: data.email, href: data.email && `mailto:${data.email}` },
    { icon: Phone, value: data.phone, href: data.phone && `tel:${data.phone}` },
    { icon: Globe, value: sourcePage, href: sourcePage },
  ].filter((f) => f.value);

  if (fields.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2">
      {fields.map((f, i) => {
        const Icon = f.icon;
        const content = (
          <span className="flex items-center gap-2 text-sm text-text-primary">
            <Icon className="h-4 w-4 text-accent" />
            <span className="truncate">{f.value}</span>
          </span>
        );
        return (
          <div
            key={i}
            className="rounded-md border border-border-subtle bg-bg-elevated-2 px-3 py-2"
          >
            {f.href ? (
              <a href={f.href} className="hover:text-accent">
                {content}
              </a>
            ) : (
              content
            )}
          </div>
        );
      })}
    </div>
  );
}
